import {Assert} from "js-vextensions";
import {RunInAction} from "web-vcore";
import {observable, untracked} from "mobx";

export class MapTileEntry {
	constructor(data: Partial<MapTileEntry>) { Object.assign(this, data); }
	id: string; // composite key: "zoom:tileX:tileY"
	zoom: number;
	tileX: number;
	tileY: number;
	imageData: string; // base64 encoded image data
	cachedAt: number; // timestamp for cache expiration
	lastAccessedAt: number; // timestamp for LRU eviction
}

// Cache settings
//export const MapTileCacheDB_maxCacheSize = 1000; // max number of tiles to cache
//export const MapTileCacheDB_cacheExpirationMs = 7 * 24 * 60 * 60 * 1000; // 7 days
export const MapTileCacheDB_maxCacheSize = 500_000; // max number of tiles to cache
export const MapTileCacheDB_cacheExpirationMs = 500 * 365 * 24 * 60 * 60 * 1000; // ~500 years

// Rate limiting settings
export const MapTileCacheDB_maxConcurrentDownloads = 50; // max simultaneous tile downloads
export const MapTileCacheDB_downloadCooldownMs = 1_000; // 1 second cooldown for each download-slot (so total requests-per-second limit equals `slotCount * 1`)

class DownloadSlot {
	isActive = false; // whether a download is currently in progress
	lastDownloadStartTime = 0; // timestamp of when the last download started
}

export interface TileDownloadQueueEntry {
	zoom: number; tileX: number; tileY: number; url: string;
	resolveFuncs: ((data: string)=>void)[];
	rejectFuncs: ((error: Error)=>void)[];
}

export class MapTileCacheDB {
	private onInitHandlers = [] as (()=>void)[];
	EnsureInitDone() {
		return new Promise<void>((resolve, reject)=>{
			if (this.db) resolve();
			else this.onInitHandlers.push(resolve);
		});
	}

	// Rate limiting: download slots and queue
	private downloadSlots: DownloadSlot[] = Array.from({length: MapTileCacheDB_maxConcurrentDownloads}, ()=>new DownloadSlot());
	downloadQueue: Array<TileDownloadQueueEntry> = [];
	private queueProcessTimer: NodeJS.Timeout | null = null;

	private db: IDBDatabase;
	async Init() {
		return new Promise<void>((resolve, reject)=>{
			const request = indexedDB.open("MapTileCache", 1);
			request.onupgradeneeded = event=>{
				const db = (event.target as IDBOpenDBRequest).result;
				const store = db.createObjectStore("tiles", {keyPath: "id"});
				store.createIndex("lastAccessedAt", "lastAccessedAt", {unique: false});
				store.createIndex("cachedAt", "cachedAt", {unique: false});
			};
			request.onerror = ()=>reject(request.error);
			request.onsuccess = ()=>{
				this.db = request.result;
				this.onInitHandlers.forEach(handler=>handler());
				resolve();
			};
		});
	}

	// Generate a unique key for a tile
	private GetTileKey(zoom: number, tileX: number, tileY: number): string {
		return `${zoom}:${tileX}:${tileY}`;
	}

	// Store a tile in the cache
	async StoreTile(zoom: number, tileX: number, tileY: number, imageData: string) {
		await this.EnsureInitDone();

		Assert(imageData.length > "data:,".length, "Image data must be non-empty.");

		const entry: MapTileEntry = {
			id: this.GetTileKey(zoom, tileX, tileY),
			zoom,
			tileX,
			tileY,
			imageData,
			cachedAt: Date.now(),
			lastAccessedAt: Date.now(),
		};

		const transaction = this.db.transaction(["tiles"], "readwrite");
		const store = transaction.objectStore("tiles");

		await new Promise<void>((resolve, reject)=>{
			const request = store.put(entry);
			request.onsuccess = ()=>resolve();
			request.onerror = ()=>reject(request.error);
		});

		// Update MobX cache
		RunInAction("MapTileCacheDB.StoreTile", ()=>{
			this.mobxData.tiles.set(entry.id, entry);
		});

		// Enforce cache size limit
		await this.EnforceCacheSizeLimit();
	}

	// Enforce cache size limit by removing least recently accessed tiles
	private async EnforceCacheSizeLimit(): Promise<void> {
		await this.EnsureInitDone();

		const transaction = this.db.transaction(["tiles"], "readwrite");
		const store = transaction.objectStore("tiles");

		return new Promise((resolve, reject)=>{
			const countRequest = store.count();
			countRequest.onsuccess = ()=>{
				const count = countRequest.result;
				if (count > MapTileCacheDB_maxCacheSize) {
					// Get all tiles sorted by lastAccessedAt
					const index = store.index("lastAccessedAt");
					const entries: MapTileEntry[] = [];
					const cursorRequest = index.openCursor();

					cursorRequest.onsuccess = event=>{
						const cursor = (event.target as IDBRequest).result;
						if (cursor) {
							entries.push(cursor.value as MapTileEntry);
							cursor.continue();
						} else {
							// Delete oldest entries
							const numToDelete = count - MapTileCacheDB_maxCacheSize;
							const entriesToDelete = entries.slice(0, numToDelete);

							for (const entry of entriesToDelete) {
								store.delete(entry.id);
								RunInAction("MapTileCacheDB.EnforceCacheSizeLimit", ()=>{
									this.mobxData.tiles.delete(entry.id);
								});
							}
							resolve();
						}
					};
					cursorRequest.onerror = ()=>reject(cursorRequest.error);
				} else {
					resolve();
				}
			};
			countRequest.onerror = ()=>reject(countRequest.error);
		});
	}

	// Clear all cached tiles
	async ClearAllData(): Promise<void> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["tiles"], "readwrite");
		const store = transaction.objectStore("tiles");

		return new Promise((resolve, reject)=>{
			const request = store.clear();
			request.onsuccess = ()=>{
				RunInAction("MapTileCacheDB.ClearAllData", ()=>{
					this.mobxData.tiles.clear();
				});
				// Clear download queue and timer
				this.downloadQueue.length = 0;
				if (this.queueProcessTimer) {
					clearTimeout(this.queueProcessTimer);
					this.queueProcessTimer = null;
				}
				resolve();
			};
			request.onerror = ()=>reject(request.error);
		});
	}

	// Find an available download slot (not active and past cooldown period)
	private FindAvailableSlot(): DownloadSlot | null {
		const now = Date.now();
		for (const slot of this.downloadSlots) {
			if (!slot.isActive && (now - slot.lastDownloadStartTime >= MapTileCacheDB_downloadCooldownMs)) {
				return slot;
			}
		}
		return null;
	}

	// Find the earliest time when a slot will become available (0 if slot available now)
	private GetTimeUntilNextSlotAvailable(): number {
		const now = Date.now();
		let earliestAvailableTime = Infinity;

		for (const slot of this.downloadSlots) {
			if (!slot.isActive) {
				const timeUntilAvailable = Math.max(0, (slot.lastDownloadStartTime + MapTileCacheDB_downloadCooldownMs) - now);
				earliestAvailableTime = Math.min(earliestAvailableTime, timeUntilAvailable);
			}
		}

		return earliestAvailableTime === Infinity ? 0 : earliestAvailableTime;
	}

	// Schedule queue processing after a delay
	private ScheduleQueueProcessing(delayMs: number) {
		// Clear any existing timer
		if (this.queueProcessTimer) {
			clearTimeout(this.queueProcessTimer);
			this.queueProcessTimer = null;
		}

		// Only schedule if there are items in the queue
		if (this.downloadQueue.length > 0 && delayMs > 0) {
			console.log(`[MapTileCache] Scheduling queue recheck in ${Math.ceil(delayMs / 1000)}s (${this.downloadQueue.length} queued)`);
			this.queueProcessTimer = setTimeout(()=>{
				this.queueProcessTimer = null;
				this.ProcessDownloadQueue();
			}, delayMs);
		}
	}

	// Process the download queue
	private ProcessDownloadQueue() {
		// Clear any pending timer since we're processing now
		if (this.queueProcessTimer) {
			clearTimeout(this.queueProcessTimer);
			this.queueProcessTimer = null;
		}

		let processedAny = false;
		while (this.downloadQueue.length > 0) {
			const slot = this.FindAvailableSlot();
			if (!slot) {
				// No available slots right now, schedule a recheck when one becomes available
				const timeUntilNextSlot = this.GetTimeUntilNextSlotAvailable();
				if (timeUntilNextSlot > 0) {
					this.ScheduleQueueProcessing(timeUntilNextSlot + 100); // Add 100ms buffer
				}
				break;
			}

			const queueItem = this.downloadQueue.pop()!; // use pop() (instead of shift), so that we process the newest entries first
			this.StartDownloadInSlot(slot, queueItem.zoom, queueItem.tileX, queueItem.tileY, queueItem.url, queueItem.resolveFuncs, queueItem.rejectFuncs);
			processedAny = true;
		}

		if (processedAny && this.downloadQueue.length > 0) {
			// We processed some but there are still items queued, ensure we'll check again
			const timeUntilNextSlot = this.GetTimeUntilNextSlotAvailable();
			if (timeUntilNextSlot > 0) {
				this.ScheduleQueueProcessing(timeUntilNextSlot + 100);
			}
		}
	}

	// Start a download in a specific slot
	private async StartDownloadInSlot(
		slot: DownloadSlot,
		zoom: number,
		tileX: number,
		tileY: number,
		url: string,
		resolveFuncs: ((data: string)=>void)[],
		rejectFuncs: ((error: Error)=>void)[]
	) {
		slot.isActive = true;
		slot.lastDownloadStartTime = Date.now();

		//console.log(`[MapTileCache] Starting download for tile ${zoom}/${tileX}/${tileY} (${this.downloadSlots.filter(s=>s.isActive).length} active downloads, ${this.downloadQueue.length} queued)`);

		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Failed to fetch tile: ${response.status} ${response.statusText}`);
			}

			const blob = await response.blob();
			const imageData = await this.BlobToBase64(blob);

			// Store in cache
			await this.StoreTile(zoom, tileX, tileY, imageData);

			for (const resolve of resolveFuncs) {
				resolve(imageData);
			}
		} catch (error) {
			console.error(`[MapTileCache] Error loading tile ${zoom}/${tileX}/${tileY}:`, error);
			for (const reject of rejectFuncs) {
				reject(error as Error);
			}
		} finally {
			slot.isActive = false;

			console.log(`[MapTileCache] Completed download for tile ${zoom}/${tileX}/${tileY} (${this.downloadSlots.filter(s=>s.isActive).length} active downloads, ${this.downloadQueue.length} queued)`);

			// Process queue to start next download if available
			this.ProcessDownloadQueue();
		}
	}

	// Load a tile from URL and cache it (with rate limiting)
	async LoadAndCacheTile(zoom: number, tileX: number, tileY: number): Promise<string> {
		const url = (()=>{
			// satellite imagery
			return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${tileY}/${tileX}`;
			// street/synthetic maps alternatives:
			//`https://tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`,
			//`https://a.basemaps.cartocdn.com/rastertiles/voyager/${zoom}/${tileX}/${tileY}.png`,
		})();

		// Check cache first
		const cachedTile = await this.GetTile(zoom, tileX, tileY);
		if (cachedTile) {
			console.log(`[MapTileCache] Cache hit for tile ${zoom}/${tileX}/${tileY}`);
			return cachedTile.imageData;
		}

		const existingItem = this.downloadQueue.find(item=>item.zoom == zoom && item.tileX == tileX && item.tileY == tileY && item.url == url)!;
		if (existingItem) {
			console.log(`[MapTileCache] Tile ${zoom}/${tileX}/${tileY} is already queued for download, waiting for it to complete`);
			// Tile is already queued for download, wait for it to complete
			return new Promise<string>((resolve, reject)=>{
				existingItem.resolveFuncs.push(resolve);
				existingItem.rejectFuncs.push(reject);
			});
		}

		console.log(`[MapTileCache] Cache miss for tile ${zoom}/${tileX}/${tileY}, queueing download`);

		// Add to queue and wait for download
		return new Promise<string>((resolve, reject)=>{
			this.downloadQueue.push({zoom, tileX, tileY, url, resolveFuncs: [resolve], rejectFuncs: [reject]});
			this.ProcessDownloadQueue();
		});
	}
	/*RemoveEntriesFromDownloadQueueMatching(zoom: number, tileX: number, tileY: number) {
		this.downloadQueue = this.downloadQueue.filter(item=>item.zoom != zoom || item.tileX != tileX || item.tileY != tileY);
	}*/

	// Convert blob to base64 data URL
	private BlobToBase64(blob: Blob): Promise<string> {
		return new Promise((resolve, reject)=>{
			const reader = new FileReader();
			reader.onloadend = ()=>resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	// Get cache statistics
	async GetCacheStats(): Promise<{totalTiles: number; oldestCachedAt: number; newestCachedAt: number}> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["tiles"], "readonly");
		const store = transaction.objectStore("tiles");

		return new Promise((resolve, reject)=>{
			const countRequest = store.count();
			countRequest.onsuccess = ()=>{
				const totalTiles = countRequest.result;

				const index = store.index("cachedAt");
				const entries: MapTileEntry[] = [];
				const cursorRequest = index.openCursor();

				cursorRequest.onsuccess = event=>{
					const cursor = (event.target as IDBRequest).result;
					if (cursor) {
						entries.push(cursor.value as MapTileEntry);
						cursor.continue();
					} else {
						const cachedAts = entries.map(e=>e.cachedAt);
						const oldestCachedAt = cachedAts.length > 0 ? Math.min(...cachedAts) : 0;
						const newestCachedAt = cachedAts.length > 0 ? Math.max(...cachedAts) : 0;
						resolve({totalTiles, oldestCachedAt, newestCachedAt});
					}
				};
				cursorRequest.onerror = ()=>reject(cursorRequest.error);
			};
			countRequest.onerror = ()=>reject(countRequest.error);
		});
	}

	// Get download queue statistics
	GetDownloadStats(): {activeDownloads: number; queuedDownloads: number; availableSlots: number; slotsOnCooldown: number} {
		const now = Date.now();
		const activeDownloads = this.downloadSlots.filter(s=>s.isActive).length;
		const slotsOnCooldown = this.downloadSlots.filter(s=>!s.isActive && (now - s.lastDownloadStartTime < MapTileCacheDB_downloadCooldownMs)).length;
		const availableSlots = this.downloadSlots.length - activeDownloads - slotsOnCooldown;
		const queuedDownloads = this.downloadQueue.length;

		return {activeDownloads, queuedDownloads, availableSlots, slotsOnCooldown};
	}

	// Get a tile from the cache
	async GetTile(zoom: number, tileX: number, tileY: number): Promise<MapTileEntry | null> {
		await this.EnsureInitDone();

		const tileKey = this.GetTileKey(zoom, tileX, tileY);
		const transaction = this.db.transaction(["tiles"], "readwrite");
		const store = transaction.objectStore("tiles");

		return new Promise((resolve, reject)=>{
			const request = store.get(tileKey);
			request.onsuccess = ()=>{
				const entry = request.result as MapTileEntry | undefined;
				if (entry) {
					// Check if cache entry has expired
					if (Date.now() - entry.cachedAt > MapTileCacheDB_cacheExpirationMs) {
						// Entry expired, delete it
						store.delete(tileKey);
						RunInAction("MapTileCacheDB.GetTile_expired", ()=>{
							this.mobxData.tiles.delete(tileKey);
						});
						resolve(null);
					} else {
						// Update last accessed time
						entry.lastAccessedAt = Date.now();
						store.put(entry);
						RunInAction("MapTileCacheDB.GetTile", ()=>{
							this.mobxData.tiles.set(tileKey, entry);
						});
						resolve(entry);
					}
				} else {
					resolve(null);
				}
			};
			request.onerror = ()=>reject(request.error);
		});
	}

	// high level
	// ==========

	FilterDownloadQueueToOnlyEntriesMatching(xFunc: (entry: TileDownloadQueueEntry)=>boolean) {
		const oldQueueSize = this.downloadQueue.length;
		const removedEntries = [] as TileDownloadQueueEntry[];
		this.downloadQueue = this.downloadQueue.filter(entry=>{
			const keep = xFunc(entry);
			if (!keep) {
				removedEntries.push(entry);
			}
			return keep;
		});
		if (removedEntries.length > 0) {
			RunInAction("MapTileCacheDB.FilterDownloadQueueToOnlyEntriesMatching", ()=>{
				// untracked; for a react-comp that trims queue-entries, we don't want it to react/rerender from content changes (eg. caused by the `tiles.has()` call in the assert below)
				untracked(()=>{
					for (const entry of removedEntries) {
						const tileKey = this.GetTileKey(entry.zoom, entry.tileX, entry.tileY);
						this.mobxData.tiles_retrievalsInProgress.set(tileKey, false); // since we canceled the download, we mark it as not in-progress (so that if it's requested again, a new download is started)
						Assert(!this.mobxData.tiles.has(tileKey), "If a tile download is canceled from the queue, it should not be in the cached tiles map.");
						//this.mobxData.tiles.delete(tileKey);
					}
				});
			});
		}
		if (this.downloadQueue.length != oldQueueSize) {
			console.log(`Queue trimmed from ${oldQueueSize} to ${this.downloadQueue.length} entries.`);
		}
	}

	mobxData = {
		//tileCache: observable.map<string, MapTileEntry>(),
		tiles_retrievalsInProgress: new Map<string, boolean>(),
		tiles: observable.map<string, MapTileEntry>(),
	};

	async GetTile_FromCacheOrRemote(zoom: number, tileX: number, tileY: number): Promise<MapTileEntry|null> {
		const tile = await this.GetTile(zoom, tileX, tileY);
		if (tile) return tile;

		// Not in cache, load image from tile-server and cache it
		const imageData = await this.LoadAndCacheTile(zoom, tileX, tileY);
		const newTile = await this.GetTile(zoom, tileX, tileY);
		return newTile;
	}

	GetTile_MobX(zoom: number, tileX: number, tileY: number): MapTileEntry | null {
		const tileKey = this.GetTileKey(zoom, tileX, tileY);
		if (!this.mobxData.tiles_retrievalsInProgress.get(tileKey) && !this.mobxData.tiles.get(tileKey)) {
			this.mobxData.tiles_retrievalsInProgress.set(tileKey, true);
			(async()=>{
				try {
					const tile = await this.GetTile_FromCacheOrRemote(zoom, tileX, tileY);
					if (tile == null) throw new Error(`Failed to retrieve tile. @tileX:${tileX} @tileY:${tileY} @zoom:${zoom}`);
					this.mobxData.tiles_retrievalsInProgress.set(tileKey, false);
					RunInAction("MemoryPalaceFrameDB.GetFramesForTarget_MobX", ()=>this.mobxData.tiles.set(tileKey, tile));
				} catch (err) {
					this.mobxData.tiles_retrievalsInProgress.set(tileKey, false);
					console.error("Error retrieving frames for target:", tileKey, err);
				}
			})();
		}
		return this.mobxData.tiles.get(tileKey) ?? null;
	}
}

export const mapTileCacheDB = new MapTileCacheDB();
mapTileCacheDB.Init();